import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { APP_SHORT_NAME } from '../constants/branding';
import DomainManager from '../components/admin/DomainManager';
import UserManager from '../components/admin/UserManager';
import WorkFunctionManager from '../components/admin/WorkFunctionManager';
import PaymentTermsManager from '../components/admin/PaymentTermsManager';
import ActivityAuditLog from '../components/admin/ActivityAuditLog';

const formatBytes = (bytes) => {
  const num = Number(bytes);
  if (Number.isNaN(num) || num < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = num;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(2)} ${units[unitIndex]}`;
};

const Admin = () => {
  const { user, isAdmin } = useAuth();
  const [storage, setStorage] = React.useState(null);
  const [storageLoading, setStorageLoading] = React.useState(true);
  const [storageError, setStorageError] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = React.useState(true);
  const [attachmentsError, setAttachmentsError] = React.useState('');
  const [attachmentsPage, setAttachmentsPage] = React.useState(1);
  const [attachmentsTotal, setAttachmentsTotal] = React.useState(0);
  const [attachmentsPageSize] = React.useState(25);
  const [packingSlipRecipientsInput, setPackingSlipRecipientsInput] = React.useState('');
  const [packingSlipRecipientsSource, setPackingSlipRecipientsSource] = React.useState('');
  const [packingSlipRecipientsSaving, setPackingSlipRecipientsSaving] = React.useState(false);
  const [packingSlipRecipientsError, setPackingSlipRecipientsError] = React.useState('');
  const [packingSlipEmailLogs, setPackingSlipEmailLogs] = React.useState([]);
  const [packingSlipEmailLogsLoading, setPackingSlipEmailLogsLoading] = React.useState(true);
  const [packingSlipEmailLogsError, setPackingSlipEmailLogsError] = React.useState('');
  const [backupsInfo, setBackupsInfo] = React.useState(null);
  const [backupsLoading, setBackupsLoading] = React.useState(true);
  const [backupsError, setBackupsError] = React.useState('');
  const [kioskEnabled, setKioskEnabled] = React.useState(true);
  const [kioskSettingsLoading, setKioskSettingsLoading] = React.useState(true);
  const [kioskSettingsSaving, setKioskSettingsSaving] = React.useState(false);
  const [kioskSettingsError, setKioskSettingsError] = React.useState('');
  const [databaseBackupEnabled, setDatabaseBackupEnabled] = React.useState(false);
  const [databaseBackupLoading, setDatabaseBackupLoading] = React.useState(true);
  const [databaseBackupSaving, setDatabaseBackupSaving] = React.useState(false);
  const [databaseBackupError, setDatabaseBackupError] = React.useState('');
  const [hostPreviewOverride, setHostPreviewOverride] = React.useState('');
  const [hostPreviewLoading, setHostPreviewLoading] = React.useState(false);
  const [hostPreviewError, setHostPreviewError] = React.useState('');
  const [hostPreviewData, setHostPreviewData] = React.useState(null);
  const [unverifiedSignups, setUnverifiedSignups] = React.useState([]);
  const [unverifiedSignupsLoading, setUnverifiedSignupsLoading] = React.useState(true);
  const [unverifiedSignupsError, setUnverifiedSignupsError] = React.useState('');

  const fetchHostPreview = React.useCallback(async (hostOpt) => {
    try {
      setHostPreviewLoading(true);
      setHostPreviewError('');
      const params = {};
      if (hostOpt != null && String(hostOpt).trim() !== '') params.host = String(hostOpt).trim();
      const res = await axios.get('/api/admin/settings/domain-host-preview', { params });
      setHostPreviewData(res?.data?.data ?? null);
    } catch (err) {
      console.error('Failed to load host preview:', err);
      setHostPreviewError(err.response?.data?.message || 'Failed to load host preview');
      setHostPreviewData(null);
    } finally {
      setHostPreviewLoading(false);
    }
  }, []);

  const reloadUnverifiedSignups = React.useCallback(async () => {
    try {
      setUnverifiedSignupsLoading(true);
      setUnverifiedSignupsError('');
      const res = await axios.get('/api/admin/signups/unverified', { params: { limit: 300 } });
      setUnverifiedSignups(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Failed to load unverified signups:', err);
      setUnverifiedSignupsError(err.response?.data?.message || 'Failed to load unverified signups');
      setUnverifiedSignups([]);
    } finally {
      setUnverifiedSignupsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const loadStorage = async () => {
      try {
        setStorageLoading(true);
        setStorageError('');
        const res = await axios.get('/api/admin/storage/attachments');
        const data = res?.data?.data ?? res?.data;
        setStorage(data || null);
      } catch (err) {
        console.error('Failed to load attachment storage stats:', err);
        setStorageError(err.response?.data?.message || 'Failed to load attachment storage stats');
      } finally {
        setStorageLoading(false);
      }
    };

    const loadPackingSlipSettings = async () => {
      try {
        setPackingSlipRecipientsError('');
        const res = await axios.get('/api/admin/settings/packing-slip-notifications');
        const data = res?.data?.data ?? res?.data;
        const listStr = Array.isArray(data?.recipients)
          ? data.recipients.join(', ')
          : (typeof data === 'string' ? data : '');
        setPackingSlipRecipientsInput(listStr);
        setPackingSlipRecipientsSource(data?.source || '');
      } catch (err) {
        console.error('Failed to load packing slip notification settings:', err);
        setPackingSlipRecipientsError(err.response?.data?.message || 'Failed to load packing slip notification settings');
      }
    };

    const loadPackingSlipEmailLogs = async () => {
      try {
        setPackingSlipEmailLogsLoading(true);
        setPackingSlipEmailLogsError('');
        const res = await axios.get('/api/admin/settings/packing-slip-notifications/logs', { params: { limit: 20 } });
        const data = res?.data?.data ?? res?.data;
        setPackingSlipEmailLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load packing slip email logs:', err);
        setPackingSlipEmailLogsError(err.response?.data?.message || 'Failed to load packing slip email logs');
      } finally {
        setPackingSlipEmailLogsLoading(false);
      }
    };

    const loadBackups = async () => {
      try {
        setBackupsLoading(true);
        setBackupsError('');
        const res = await axios.get('/api/admin/storage/backups', { params: { limit: 100 } });
        const data = res?.data?.data ?? res?.data;
        setBackupsInfo(data || null);
      } catch (err) {
        console.error('Failed to load backups info:', err);
        setBackupsError(err.response?.data?.message || 'Failed to load backups info');
      } finally {
        setBackupsLoading(false);
      }
    };

    const loadKioskSettings = async () => {
      try {
        setKioskSettingsLoading(true);
        setKioskSettingsError('');
        const res = await axios.get('/api/admin/settings/kiosk');
        const data = res?.data?.data ?? res?.data;
        setKioskEnabled(data?.enabled !== false);
      } catch (err) {
        console.error('Failed to load kiosk settings:', err);
        setKioskSettingsError(err.response?.data?.message || 'Failed to load kiosk settings');
      } finally {
        setKioskSettingsLoading(false);
      }
    };

    const loadDatabaseBackupSettings = async () => {
      try {
        setDatabaseBackupLoading(true);
        setDatabaseBackupError('');
        const res = await axios.get('/api/admin/settings/database-backup');
        const data = res?.data?.data ?? res?.data;
        setDatabaseBackupEnabled(data?.enabled === true);
      } catch (err) {
        console.error('Failed to load database backup setting:', err);
        setDatabaseBackupError(err.response?.data?.message || 'Failed to load database backup setting');
      } finally {
        setDatabaseBackupLoading(false);
      }
    };

    if (isAdmin()) {
      loadStorage();
      loadPackingSlipSettings();
      loadPackingSlipEmailLogs();
      loadBackups();
      loadDatabaseBackupSettings();
      loadKioskSettings();
      reloadUnverifiedSignups();
      fetchHostPreview('');
      // Initial attachments load
      (async () => {
        try {
          setAttachmentsLoading(true);
          setAttachmentsError('');
          const res = await axios.get('/api/admin/storage/attachments/list', {
            params: { page: 1, pageSize: attachmentsPageSize }
          });
          const data = res?.data?.data ?? res?.data;
          setAttachments(data?.items || []);
          setAttachmentsTotal(data?.pagination?.total || 0);
          setAttachmentsPage(data?.pagination?.page || 1);
        } catch (err) {
          console.error('Failed to load attachments list:', err);
          setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
        } finally {
          setAttachmentsLoading(false);
        }
      })();
    } else {
      setUnverifiedSignupsLoading(false);
    }
  }, [isAdmin, fetchHostPreview, reloadUnverifiedSignups]);

  // Only allow admin users to access this page
  if (!isAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage system settings and configurations
      </Typography>

      <Grid container spacing={3}>
        {/* User Management */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="User Management"
              subheader="Manage employees, managers, and administrators"
            />
            <Divider />
            <CardContent>
              <UserManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Self-serve signups: email not yet verified */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Unverified signups"
              subheader="Accounts that started registration but have not completed email verification. Listing is global (not limited to your current organization)."
              action={
                <Button
                  size="small"
                  variant="outlined"
                  disabled={unverifiedSignupsLoading}
                  onClick={() => reloadUnverifiedSignups()}
                >
                  Refresh
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {unverifiedSignupsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {unverifiedSignupsError}
                </Alert>
              )}
              {unverifiedSignupsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </Box>
              ) : unverifiedSignups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No pending email verifications. Everyone who signed up has verified their address (or only
                  platform-level accounts are unverified).
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name / org (from signup)</TableCell>
                        <TableCell>Signed up</TableCell>
                        <TableCell>Active invite</TableCell>
                        <TableCell align="right">Employee rows</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unverifiedSignups.map((row) => {
                        const pending = row.pending_registration || {};
                        const nameParts = [pending.first_name, pending.last_name].filter(Boolean).join(' ').trim();
                        const orgHint = pending.organization_name || pending.organizationName || '';
                        const signupLabel = [nameParts, orgHint].filter(Boolean).join(' · ') || '—';
                        const hasInvite = Number(row.active_invite_count) > 0;
                        const exp = row.active_invite_expires_at
                          ? new Date(row.active_invite_expires_at).toLocaleString()
                          : '';
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.email}</TableCell>
                            <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {signupLabel}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {hasInvite ? `Yes${exp ? ` (expires ${exp})` : ''}` : 'No'}
                            </TableCell>
                            <TableCell align="right">{row.employee_count ?? 0}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Activity audit log */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Activity audit log"
              subheader="Who signed in or out, clock punches, and authenticated saves while signed in"
            />
            <Divider />
            <CardContent>
              <ActivityAuditLog />
            </CardContent>
          </Card>
        </Grid>

        {/* Domain Management */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Domain Management"
              subheader={`Configure domains that can access this ${APP_SHORT_NAME} instance`}
            />
            <Divider />
            <CardContent>
              <DomainManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Host → tenant bootstrap (same logic as public GET /api/domains without auth) */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="API host preview"
              subheader="Shows normalized Host headers, resolved org, domain list, kiosk flag, and packing-slip default recipients — matching what unauthenticated bootstrap and public kiosk checks use for a given host."
            />
            <Divider />
            <CardContent>
              {hostPreviewError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {hostPreviewError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  label="Override Host (optional)"
                  placeholder="e.g. app.customer.com"
                  value={hostPreviewOverride}
                  onChange={(e) => setHostPreviewOverride(e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={hostPreviewLoading}
                  onClick={() => fetchHostPreview(hostPreviewOverride)}
                >
                  {hostPreviewLoading ? 'Loading…' : 'Run preview'}
                </Button>
              </Box>
              {hostPreviewLoading && !hostPreviewData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </Box>
              ) : hostPreviewData ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 420
                  }}
                >
                  {JSON.stringify(hostPreviewData, null, 2)}
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* System Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="System Information" />
            <Divider />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Current User
                </Typography>
                <Typography variant="body1">
                  {user?.first_name} {user?.last_name} ({user?.email})
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="body1">
                  {user?.role}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Domain
                </Typography>
                <Typography variant="body1">
                  {typeof window !== 'undefined' ? window.location.origin : 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Credit / payment terms (customer & invoice dropdowns) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Credit terms (payment terms)"
              subheader="Options for customer credit and invoice payment terms (e.g. Net 30, Net 20)"
            />
            <Divider />
            <CardContent>
              <PaymentTermsManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Kiosk (shop clock in/out) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Kiosk mode"
              subheader="Per-organization setting for shop floor clock in/out (PIN punches and the login “Enter Kiosk Mode” option). Public checks resolve the org from the request hostname; legacy global default still applies if unset."
            />
            <Divider />
            <CardContent>
              {kioskSettingsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {kioskSettingsError}
                </Alert>
              )}
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={kioskEnabled}
                    disabled={kioskSettingsLoading || kioskSettingsSaving}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setKioskSettingsSaving(true);
                      setKioskSettingsError('');
                      try {
                        await axios.put('/api/admin/settings/kiosk', { enabled: next });
                        setKioskEnabled(next);
                      } catch (err) {
                        console.error('Failed to save kiosk settings:', err);
                        setKioskSettingsError(err.response?.data?.message || 'Failed to save kiosk settings');
                      } finally {
                        setKioskSettingsSaving(false);
                      }
                    }}
                  />
                )}
                label="Enable kiosk mode"
              />
              {kioskSettingsSaving && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Saving…
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Packing slip notifications (default recipients) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Packing Slip Notifications" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Default email recipients for packing slip notifications (saved for this organization; falls back to legacy global defaults if none are set). Customers can override this list individually.
              </Typography>
              {packingSlipRecipientsSource && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Currently loaded from:{' '}
                  {packingSlipRecipientsSource === 'org'
                    ? 'organization settings'
                    : packingSlipRecipientsSource === 'legacy'
                      ? 'legacy global default (migrate by saving above)'
                      : 'none (empty until you save or legacy exists)'}
                </Typography>
              )}
              {packingSlipRecipientsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {packingSlipRecipientsError}
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="Default recipient emails (comma-separated)"
                value={packingSlipRecipientsInput}
                onChange={(e) => setPackingSlipRecipientsInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={packingSlipRecipientsSaving}
                onClick={async () => {
                  try {
                    setPackingSlipRecipientsSaving(true);
                    setPackingSlipRecipientsError('');
                    const recipients = packingSlipRecipientsInput
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    await axios.put('/api/admin/settings/packing-slip-notifications', {
                      recipients
                    });
                    setPackingSlipRecipientsSource('org');
                  } catch (err) {
                    console.error('Failed to save packing slip notification settings:', err);
                    setPackingSlipRecipientsError(err.response?.data?.message || 'Failed to save settings');
                  } finally {
                    setPackingSlipRecipientsSaving(false);
                  }
                }}
              >
                {packingSlipRecipientsSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent sent email logs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Sent Packing Slip Emails (Last 20)" />
            <Divider />
            <CardContent>
              {packingSlipEmailLogsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {packingSlipEmailLogsError}
                </Alert>
              )}

              {packingSlipEmailLogsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </Box>
              ) : packingSlipEmailLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No email logs yet.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Time
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Status
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Customer
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {packingSlipEmailLogs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                            {log.status || '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{ fontWeight: 600 }}>{log.customer_name || '—'}</div>
                            <div style={{ color: '#666', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.subject || ''}
                            </div>
                            {log.error ? (
                              <div style={{ color: 'crimson', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.error}>
                                {log.error}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Function management (work_functions) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Functions" subheader="Configure the Function dropdown in Time Entries" />
            <Divider />
            <CardContent>
              <WorkFunctionManager hideHeader />
            </CardContent>
          </Card>
        </Grid>

        {/* Attachments / Uploads Storage */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Uploads / Attachments Storage"
              subheader="Monitor disk usage for the attachments directory on the server"
            />
            <Divider />
            <CardContent>
              {storageLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading storage stats…</Typography>
                </Box>
              ) : storageError ? (
                <Alert severity="error">{storageError}</Alert>
              ) : !storage ? (
                <Typography variant="body2" color="text.secondary">
                  Storage information is not available.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {storage.storage_type === 's3' && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Storage
                      </Typography>
                      <Typography variant="body2">S3</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {storage.storage_type === 's3' ? 'S3 location' : 'Attachments directory'}
                    </Typography>
                    <Typography variant="body2">
                      {storage.base_dir || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Files
                    </Typography>
                    <Typography variant="body2">
                      {storage.files?.count ?? 0} file(s)
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Space Used by Attachments
                    </Typography>
                    <Typography variant="body2">
                      {formatBytes(storage.files?.total_bytes ?? 0)}
                    </Typography>
                  </Box>
                  {storage.disk && storage.disk.total_bytes != null && (
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Disk Total
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(storage.disk.total_bytes)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Disk Available
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(storage.disk.available_bytes)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Filesystem / Mount
                        </Typography>
                        <Typography variant="body2">
                          {storage.disk.filesystem} ({storage.disk.mount_point})
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Attachments File Manager */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Attachments File Manager"
              subheader="Browse uploaded attachments with customer, quote, and order context"
            />
            <Divider />
            <CardContent>
              {attachmentsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {attachmentsError}
                </Alert>
              )}
              {attachmentsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading attachments…</Typography>
                </Box>
              ) : attachments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No attachments found.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>File</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Quote</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Order</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Size</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Uploaded</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>By</th>
                        <th style={{ width: 48 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((att) => (
                        <tr key={att.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.file_name}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.customer_id ? (
                              <Link
                                to={`/customers/${att.customer_id}`}
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {att.customer_number != null
                                  ? `${att.customer_number} – ${att.company_name || ''}`
                                  : att.company_name || `Customer #${att.customer_id}`}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.quote_id ? (
                              <Link to={`/quotes/${att.quote_id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                {att.quote_identifier != null ? att.quote_identifier : att.quote_number != null ? att.quote_number : att.quote_id}
                              </Link>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.work_order_id ? (
                              <Link to={`/work-orders/${att.work_order_id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                {att.work_order_identifier != null ? att.work_order_identifier : att.work_order_number != null ? att.work_order_number : att.work_order_id}
                              </Link>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {formatBytes(att.file_size || 0)}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.uploaded_at
                              ? new Date(att.uploaded_at).toLocaleString()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.uploaded_by_first_name || att.uploaded_by_last_name
                              ? `${att.uploaded_by_first_name || ''} ${att.uploaded_by_last_name || ''}`.trim()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete attachment"
                              onClick={async () => {
                                if (!window.confirm(`Delete attachment "${att.file_name}"? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`/api/attachments/${att.id}`);
                                  setAttachments((prev) => prev.filter((x) => x.id !== att.id));
                                  setAttachmentsTotal((prev) => Math.max(prev - 1, 0));
                                  try {
                                    const storageRes = await axios.get('/api/admin/storage/attachments');
                                    const storageData = storageRes?.data?.data ?? storageRes?.data;
                                    setStorage(storageData || null);
                                  } catch (err) {
                                    console.error('Failed to refresh storage stats after delete:', err);
                                  }
                                } catch (err) {
                                  console.error('Failed to delete attachment:', err);
                                  setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {(attachmentsPage - 1) * attachmentsPageSize + 1}–
                      {Math.min(attachmentsPage * attachmentsPageSize, attachmentsTotal)} of {attachmentsTotal}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={attachmentsPage <= 1}
                        onClick={async () => {
                          if (attachmentsPage <= 1) return;
                          const newPage = attachmentsPage - 1;
                          try {
                            setAttachmentsLoading(true);
                            const res = await axios.get('/api/admin/storage/attachments/list', {
                              params: { page: newPage, pageSize: attachmentsPageSize }
                            });
                            const data = res?.data?.data ?? res?.data;
                            setAttachments(data?.items || []);
                            setAttachmentsTotal(data?.pagination?.total || 0);
                            setAttachmentsPage(data?.pagination?.page || newPage);
                          } catch (err) {
                            console.error('Failed to load attachments list:', err);
                            setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
                          } finally {
                            setAttachmentsLoading(false);
                          }
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={attachmentsPage * attachmentsPageSize >= attachmentsTotal}
                        onClick={async () => {
                          if (attachmentsPage * attachmentsPageSize >= attachmentsTotal) return;
                          const newPage = attachmentsPage + 1;
                          try {
                            setAttachmentsLoading(true);
                            const res = await axios.get('/api/admin/storage/attachments/list', {
                              params: { page: newPage, pageSize: attachmentsPageSize }
                            });
                            const data = res?.data?.data ?? res?.data;
                            setAttachments(data?.items || []);
                            setAttachmentsTotal(data?.pagination?.total || 0);
                            setAttachmentsPage(data?.pagination?.page || newPage);
                          } catch (err) {
                            console.error('Failed to load attachments list:', err);
                            setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
                          } finally {
                            setAttachmentsLoading(false);
                          }
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Database Backups */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Database Backups"
              subheader="Same S3 bucket as Fabworks/WorkManager (separate prefix). Nightly job runs inside the API when enabled below."
            />
            <Divider />
            <CardContent>
              {databaseBackupError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {databaseBackupError}
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                When on, the server runs <code>pg_dump</code> on a schedule (default 2:00 daily), gzips the dump,
                uploads to the backup bucket/prefix below, and keeps the newest backups (same pattern as WorkManager).
                Requires <code>pg_dump</code> on the server and AWS credentials with S3 access to that bucket.
              </Alert>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={databaseBackupEnabled}
                    disabled={databaseBackupLoading || databaseBackupSaving}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setDatabaseBackupSaving(true);
                      setDatabaseBackupError('');
                      try {
                        await axios.put('/api/admin/settings/database-backup', { enabled: next });
                        setDatabaseBackupEnabled(next);
                        try {
                          setBackupsLoading(true);
                          const res = await axios.get('/api/admin/storage/backups', { params: { limit: 100 } });
                          const data = res?.data?.data ?? res?.data;
                          setBackupsInfo(data || null);
                        } catch (err) {
                          console.error('Failed to refresh backups info:', err);
                        } finally {
                          setBackupsLoading(false);
                        }
                      } catch (err) {
                        console.error('Failed to save database backup setting:', err);
                        setDatabaseBackupError(err.response?.data?.message || 'Failed to save');
                      } finally {
                        setDatabaseBackupSaving(false);
                      }
                    }}
                  />
                )}
                label="Automated S3 database backups"
              />
              {databaseBackupSaving && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Saving…
                </Typography>
              )}
              {backupsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading backups…</Typography>
                </Box>
              ) : backupsError ? (
                <Alert severity="error">{backupsError}</Alert>
              ) : !backupsInfo ? (
                <Typography variant="body2" color="text.secondary">
                  Backup information is not available.
                </Typography>
              ) : (
                <Box>
                  {backupsInfo.app_scheduler && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Built-in scheduler
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        Cron: {backupsInfo.app_scheduler.schedule || '—'}
                        {backupsInfo.app_scheduler.timezone
                          ? ` (${backupsInfo.app_scheduler.timezone})`
                          : ' (server local time)'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Admin toggle:{' '}
                        {backupsInfo.app_scheduler.enabled ? 'on' : 'off'}
                        {' · '}
                        Keep last {backupsInfo.app_scheduler.keep_last_backups ?? backupsInfo.retention?.keep_last_backups ?? 90}
                      </Typography>
                      {backupsInfo.app_scheduler.last_status && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Last run:{' '}
                          {backupsInfo.app_scheduler.last_status.at
                            ? new Date(backupsInfo.app_scheduler.last_status.at).toLocaleString()
                            : '—'}
                          {' — '}
                          {backupsInfo.app_scheduler.last_status.ok ? 'OK' : 'Failed'}
                          {backupsInfo.app_scheduler.last_status.message
                            ? `: ${backupsInfo.app_scheduler.last_status.message}`
                            : ''}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Bucket</Typography>
                      <Typography variant="body2">{backupsInfo.bucket || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Prefix</Typography>
                      <Typography variant="body2">{backupsInfo.prefix || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Retention</Typography>
                      <Typography variant="body2">Keep last {backupsInfo.retention?.keep_last_backups ?? 90}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Cron status</Typography>
                      <Typography variant="body2">
                        {backupsInfo.cron?.configured ? 'Configured' : 'Not detected'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Expected schedule
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {backupsInfo.cron?.expected_schedule || '—'}
                  </Typography>
                  {backupsInfo.cron?.detected_line && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Detected on server
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {backupsInfo.cron.detected_line}
                      </Typography>
                    </>
                  )}

                  {Array.isArray(backupsInfo.backups) && backupsInfo.backups.length > 0 ? (
                    <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>File</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Size</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Last Modified</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Checksum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backupsInfo.backups.map((b) => (
                            <tr key={b.key}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{b.filename || b.key}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{formatBytes(b.size_bytes || 0)}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {b.last_modified ? new Date(b.last_modified).toLocaleString() : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {b.has_checksum ? 'Yes' : 'No'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No backups found in this S3 prefix yet.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Admin;
